import { User, LoginCredentials, UserRole } from '../types';
import apiService from './api';

export class AuthService {
  async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    const response = await apiService.post<{ user: User; token: string }>(
      '/api/auth/login',
      credentials
    );

    if (!this.isInternalUser(response.user)) {
      throw new Error(
        'Access denied. Only sys_admin and sys_support users can access this dashboard.'
      );
    }

    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));

    return response;
  }

  async logout(): Promise<void> {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return null;

      const user = JSON.parse(storedUser) as User;
      if (!this.isInternalUser(user)) {
        await this.logout();
        return null;
      }

      return user;
    } catch {
      return null;
    }
  }

  getCurrentUserSync(): User | null {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return null;

      const user = JSON.parse(storedUser) as User;
      if (!this.isInternalUser(user)) {
        // Don't call logout() here as it's async and this is sync
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return null;
      }

      return user;
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUserSync();
    return !!(token && user);
  }

  private isInternalUser(user: User): boolean {
    return user.role === UserRole.SYS_ADMIN || user.role === UserRole.SYS_SUPPORT;
  }

  hasPermission(user: User, permission: string): boolean {
    if (user.role === UserRole.SYS_ADMIN) {
      return true;
    }

    if (user.role === UserRole.SYS_SUPPORT) {
      const supportPermissions = [
        'users:read',
        'users:approve',
        'users:reject',
        'organizations:read',
        'branches:read',
        'campaigns:read',
        'jobs:read',
        'reservations:read',
        'dashboard:read',
      ];
      return supportPermissions.includes(permission);
    }

    return false;
  }
}

export const authService = new AuthService();
export default authService;
